import type { CSSProperties, FC } from 'react'

import { monotonePath, scalePoints } from '@/lib/chart'
import { CountUp } from '@/components/kit/count-up'
import { InView } from '@/components/kit/in-view'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { scoreboard } from '@/constants/home'

const labels = ['Launched', 'Retention', 'Views', 'Turnaround']

export const Scoreboard: FC = () => {
    return (
        <section
            id='scoreboard'
            aria-labelledby='scoreboard-title'
            className='px-3 md:px-5'
        >
            <div className='grain grain-light reg-marks-light relative overflow-hidden rounded-[2rem] bg-ink px-5 py-20 text-cream [--grain-opacity:0.25] md:rounded-[3rem] md:px-12 md:py-28'>
                <div className='mx-auto max-w-[1300px]'>
                    <div className='flex flex-wrap items-end justify-between gap-6 border-b border-cream/15 pb-8'>
                        <div>
                            <SectionLabel
                                index='08'
                                tone='cream'
                            >
                                Numbers, lightly roasted
                            </SectionLabel>
                            <SplitText
                                as='h2'
                                id='scoreboard-title'
                                lines={['The Scoreboard']}
                                italic='scoreboard'
                                className='mt-4 text-[clamp(2.75rem,6vw,5.75rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                            />
                        </div>
                        <div className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60'>
                            <span className='size-2 animate-blink rounded-full bg-ember' />
                            Season 2026 · updated quarterly
                        </div>
                    </div>

                    <div className='grid sm:grid-cols-2 lg:grid-cols-4'>
                        {scoreboard.map((stat, index) => {
                            const points = scalePoints(stat.points, 300, 140, { pad: 6 })

                            return (
                                <Reveal
                                    key={labels[index]}
                                    delay={index * 100}
                                    className='relative overflow-hidden border-b border-cream/15 py-10 sm:px-6 sm:[&:nth-child(odd)]:border-r lg:border-b-0 lg:border-r lg:last:border-r-0 lg:first:pl-0'
                                >
                                    <InView className='pointer-events-none absolute inset-x-0 bottom-0 h-[70%] opacity-60'>
                                        <svg
                                            viewBox='0 0 300 140'
                                            preserveAspectRatio='none'
                                            className='h-full w-full overflow-visible'
                                            aria-hidden='true'
                                        >
                                            <path
                                                className='draw-path'
                                                pathLength={1}
                                                d={monotonePath(points)}
                                                fill='none'
                                                stroke='#ff5c00'
                                                strokeWidth='1.5'
                                                vectorEffect='non-scaling-stroke'
                                                style={{ '--draw-delay': `${300 + index * 150}ms` } as CSSProperties}
                                            />
                                        </svg>
                                    </InView>

                                    <p className='relative font-mono text-[11px] uppercase tracking-[0.16em] text-ember'>
                                        {String(index + 1).padStart(2, '0')} · {labels[index]}
                                    </p>
                                    <p className='relative mt-4 font-heading text-[clamp(4.5rem,8vw,7.5rem)] font-bold leading-[0.85] tracking-[-0.05em]'>
                                        <CountUp
                                            value={stat.value}
                                            decimals={stat.decimals}
                                            suffix={stat.suffix}
                                        />
                                    </p>
                                    <p className='relative mt-6 max-w-[17rem] text-[15px] leading-relaxed text-cream/75'>
                                        {stat.caption}
                                    </p>
                                </Reveal>
                            )
                        })}
                    </div>
                </div>
            </div>
        </section>
    )
}
